export type FieldCatalogCategory = 'layout' | 'basic' | 'intermediate' | 'advanced';

export type FieldCatalogIcon =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'number'
  | 'checkbox'
  | 'checkbox_group'
  | 'radio_group'
  | 'select'
  | 'search'
  | 'switch'
  | 'single_choice_grid'
  | 'multi_choice_grid'
  | 'number_grid'
  | 'date'
  | 'time'
  | 'date_range'
  | 'slider'
  | 'image_picker'
  | 'range_slider'
  | 'image'
  | 'camera'
  | 'geo'
  | 'voice'
  | 'file'
  | 'signature'
  | 'heading'
  | 'paragraph'
  | 'divider'
  | 'spacer';

export interface FieldCatalogItem {
  id: string;
  label: string;
  description: string;
  category: FieldCatalogCategory;
  promptHint: string;
  icon: FieldCatalogIcon;
  /** When false, shown in the palette as Coming soon (not addable). */
  available: boolean;
  /** Lowercase search aliases for palette filter (not shown in UI). */
  tags: string[];
}

const AVAILABLE = true;
const COMING_SOON = false;

export const FIELD_CATALOG: FieldCatalogItem[] = [
  // Layout
  {
    id: 'heading',
    label: 'Heading',
    description: 'Heading to structure the form.',
    category: 'layout',
    promptHint: 'Add a heading',
    icon: 'heading',
    available: AVAILABLE,
    tags: ['section', 'title', 'header', 'structure', 'h1', 'h2'],
  },
  {
    id: 'paragraph',
    label: 'Paragraph',
    description: 'Supporting body text or instructions.',
    category: 'layout',
    promptHint: 'Add a paragraph of supporting text',
    icon: 'paragraph',
    available: AVAILABLE,
    tags: ['text', 'instructions', 'body', 'help', 'description', 'content'],
  },
  {
    id: 'divider',
    label: 'Divider',
    description: 'Horizontal line to separate sections.',
    category: 'layout',
    promptHint: 'Add a divider between sections',
    icon: 'divider',
    available: AVAILABLE,
    tags: ['separator', 'line', 'hr', 'break', 'rule'],
  },
  {
    id: 'spacer',
    label: 'Spacer',
    description: 'Vertical space between form elements.',
    category: 'layout',
    promptHint: 'Add a spacer for vertical spacing',
    icon: 'spacer',
    available: AVAILABLE,
    tags: ['space', 'gap', 'padding', 'vertical', 'margin'],
  },
  // Basic
  {
    id: 'text',
    label: 'Small text',
    description: 'Single-line field for names, titles, and short answers.',
    category: 'basic',
    promptHint: 'Add a small text field',
    icon: 'text',
    available: AVAILABLE,
    tags: ['input', 'name', 'short', 'single-line', 'string', 'textbox', 'small text'],
  },
  {
    id: 'textarea',
    label: 'Long text',
    description: 'Multi-line field for comments, notes, and longer responses.',
    category: 'basic',
    promptHint: 'Add a long text field',
    icon: 'textarea',
    available: AVAILABLE,
    tags: ['multiline', 'comment', 'notes', 'message', 'long', 'feedback', 'long text'],
  },
  {
    id: 'email',
    label: 'Email',
    description: 'Validated email address input.',
    category: 'basic',
    promptHint: 'Add an email field',
    icon: 'email',
    available: AVAILABLE,
    tags: ['mail', 'contact', 'address', 'inbox'],
  },
  {
    id: 'phone',
    label: 'Phone number',
    description: 'Phone number with optional country formatting.',
    category: 'basic',
    promptHint: 'Add a phone number field',
    icon: 'phone',
    available: AVAILABLE,
    tags: ['mobile', 'cell', 'telephone', 'contact', 'call', 'sms'],
  },
  {
    id: 'number',
    label: 'Number input',
    description: 'Numeric values with optional min and max.',
    category: 'basic',
    promptHint: 'Add a number input field',
    icon: 'number',
    available: AVAILABLE,
    tags: ['numeric', 'integer', 'decimal', 'quantity', 'amount', 'digit'],
  },
  {
    id: 'currency',
    label: 'Currency input',
    description: 'Money amounts with currency formatting.',
    category: 'basic',
    promptHint: 'Add a currency input field',
    icon: 'number',
    available: AVAILABLE,
    tags: ['money', 'price', 'amount', 'dollar', 'payment', 'cost'],
  },
  {
    id: 'url',
    label: 'URL',
    description: 'Web address with URL validation.',
    category: 'basic',
    promptHint: 'Add a URL field',
    icon: 'text',
    available: AVAILABLE,
    tags: ['link', 'website', 'web', 'address', 'http', 'domain'],
  },
  {
    id: 'checkbox',
    label: 'Checkbox',
    description: 'Yes/no consent or a single acknowledgement.',
    category: 'basic',
    promptHint: 'Add a checkbox field',
    icon: 'checkbox',
    available: AVAILABLE,
    tags: ['yes', 'no', 'consent', 'agree', 'boolean', 'tick', 'acknowledge'],
  },
  {
    id: 'checkbox_group',
    label: 'Checkbox group',
    description: 'Select one or more options from a list.',
    category: 'basic',
    promptHint: 'Add a checkbox group field',
    icon: 'checkbox_group',
    available: AVAILABLE,
    tags: ['multi', 'select', 'options', 'list', 'multiple', 'choices'],
  },
  {
    id: 'radio_group',
    label: 'Radio group',
    description: 'Pick exactly one option from a short list.',
    category: 'basic',
    promptHint: 'Add a radio group field',
    icon: 'radio_group',
    available: AVAILABLE,
    tags: ['single', 'choice', 'options', 'pick', 'one', 'select'],
  },
  {
    id: 'switch',
    label: 'Switch',
    description: 'Toggle an option on or off.',
    category: 'basic',
    promptHint: 'Add a switch field',
    icon: 'switch',
    available: AVAILABLE,
    tags: ['toggle', 'on', 'off', 'boolean', 'enable', 'disable'],
  },
  {
    id: 'select',
    label: 'Dropdown',
    description: 'Select one value from a compact dropdown menu.',
    category: 'basic',
    promptHint: 'Add a dropdown field',
    icon: 'select',
    available: AVAILABLE,
    tags: ['dropdown', 'pick', 'menu', 'choice', 'select', 'list'],
  },
  {
    id: 'searchable_picker',
    label: 'Searchable picker',
    description:
      'Search and pick one item from a large collection with primary and secondary text.',
    category: 'basic',
    promptHint: 'Add a searchable picker field',
    icon: 'search',
    available: AVAILABLE,
    tags: ['lookup', 'search', 'picker', 'collection', 'popup', 'autocomplete'],
  },
  {
    id: 'single_choice',
    label: 'Single choice',
    description: 'Choose one option from a set of choices.',
    category: 'basic',
    promptHint: 'Add a single choice field',
    icon: 'radio_group',
    available: AVAILABLE,
    tags: ['radio', 'pick', 'one', 'option', 'choice', 'select'],
  },
  {
    id: 'multi_choice',
    label: 'Multi choice',
    description: 'Choose multiple options from a set of choices.',
    category: 'basic',
    promptHint: 'Add a multi choice field',
    icon: 'checkbox',
    available: AVAILABLE,
    tags: ['checkbox', 'multiple', 'options', 'choices', 'multi', 'select'],
  },
  {
    id: 'single_choice_grid',
    label: 'Choice Grid',
    description: 'Create options and choices',
    category: 'basic',
    promptHint: 'Add a choice grid field',
    icon: 'single_choice_grid',
    available: AVAILABLE,
    tags: ['choice', 'grid', 'choices', 'options', 'collections'],
  },
  {
    id: 'multi_choice_grid',
    label: 'Multi Choice Grid',
    description: 'Allow respondents to select multiple options for each row in a grid.',
    category: 'basic',
    promptHint: 'Create a multi-choice grid with row items and checkbox options.',
    icon: 'multi_choice_grid',
    available: AVAILABLE,
    tags: [
      'multi choice',
      'multiple choice',
      'checkbox',
      'grid',
      'table',
      'rows',
      'columns',
      'survey',
      'multiple selection',
    ],
  },
  {
    id: 'number_grid',
    label: 'Number Grid',
    description: 'Collect numeric values for multiple items across configurable columns.',
    category: 'basic',
    promptHint: 'Add a number grid field',
    icon: 'number_grid',
    available: AVAILABLE,
    tags: [
      'number',
      'grid',
      'numeric',
      'table',
      'quantity',
      'multiple inputs',
      'decimal',
      'data entry',
    ],
  },
  // Intermediate
  {
    id: 'date',
    label: 'Date picker',
    description: 'Choose a calendar date.',
    category: 'intermediate',
    promptHint: 'Add a date picker field',
    icon: 'date',
    available: AVAILABLE,
    tags: ['calendar', 'day', 'birthday', 'schedule', 'when'],
  },
  {
    id: 'time',
    label: 'Time picker',
    description: 'Choose a time of day.',
    category: 'intermediate',
    promptHint: 'Add a time picker field',
    icon: 'time',
    available: COMING_SOON,
    tags: ['clock', 'hour', 'minute', 'schedule', 'when'],
  },
  {
    id: 'datetime',
    label: 'Date & time picker',
    description: 'Choose both a date and a time.',
    category: 'intermediate',
    promptHint: 'Add a date and time picker field',
    icon: 'date',
    available: COMING_SOON,
    tags: ['calendar', 'timestamp', 'schedule', 'datetime', 'appointment'],
  },
  {
    id: 'date_range',
    label: 'Date range picker',
    description: 'Choose a start and end date.',
    category: 'intermediate',
    promptHint: 'Add a date range picker field',
    icon: 'date',
    available: AVAILABLE,
    tags: ['period', 'start', 'end', 'between', 'range', 'calendar'],
  },
  {
    id: 'time_range',
    label: 'Time range picker',
    description: 'Choose a start and end time.',
    category: 'intermediate',
    promptHint: 'Add a time range picker field',
    icon: 'time',
    available: COMING_SOON,
    tags: ['period', 'start', 'end', 'between', 'range', 'hours'],
  },
  {
    id: 'slider',
    label: 'Slider',
    description: 'Pick a numeric value along a track.',
    category: 'intermediate',
    promptHint: 'Add a slider field',
    icon: 'number',
    available: AVAILABLE,
    tags: ['range', 'track', 'scale', 'numeric', 'value'],
  },
  {
    id: 'rating',
    label: 'Rating',
    description: 'Rate with stars or a numeric scale.',
    category: 'intermediate',
    promptHint: 'Add a rating field',
    icon: 'number',
    available: AVAILABLE,
    tags: ['stars', 'score', 'scale', 'feedback', 'nps', 'review'],
  },
  {
    id: 'range_slider',
    label: 'Range slider',
    description: 'Pick a numeric range along a track.',
    category: 'intermediate',
    promptHint: 'Add a range slider field',
    icon: 'number',
    available: AVAILABLE,
    tags: ['min', 'max', 'between', 'track', 'scale', 'numeric'],
  },
  {
    id: 'color',
    label: 'Color picker',
    description: 'Choose a color value.',
    category: 'intermediate',
    promptHint: 'Add a color picker field',
    icon: 'text',
    available: COMING_SOON,
    tags: ['picker', 'hex', 'swatch', 'palette', 'rgb'],
  },
  {
    id: 'numeric_stepper',
    label: 'Numeric stepper',
    description: 'Increment or decrement a number with buttons.',
    category: 'intermediate',
    promptHint: 'Add a numeric stepper field',
    icon: 'number',
    available: COMING_SOON,
    tags: ['increment', 'decrement', 'counter', 'plus', 'minus', 'quantity'],
  },
  // Advanced
  {
    id: 'image_picker',
    label: 'Image picker',
    description: 'Upload or select an image from the device.',
    category: 'advanced',
    promptHint: 'Add an image picker field',
    icon: 'image_picker',
    available: AVAILABLE,
    tags: ['photo', 'picture', 'upload', 'gallery', 'media'],
  },
  {
    id: 'camera',
    label: 'Live camera',
    description: 'Capture a photo with the device camera.',
    category: 'advanced',
    promptHint: 'Add a live camera picture field',
    icon: 'camera',
    available: AVAILABLE,
    tags: ['photo', 'picture', 'capture', 'live', 'selfie', 'snap'],
  },
  {
    id: 'live_geo_coordinates',
    label: 'Live geo location',
    description: "Capture the respondent's current coordinates.",
    category: 'advanced',
    promptHint: 'Add a live geo coordinates field',
    icon: 'geo',
    available: COMING_SOON,
    tags: ['gps', 'location', 'geo', 'coordinates', 'geolocation', 'position'],
  },
  {
    id: 'address_selector',
    label: 'Address search',
    description: 'Search and select a postal address.',
    category: 'advanced',
    promptHint: 'Add an address search field',
    icon: 'geo',
    available: AVAILABLE,
    tags: ['address', 'postal', 'place', 'autocomplete', 'street', 'location'],
  },
  {
    id: 'map',
    label: 'Map location picker',
    description: 'Pick a location on a map.',
    category: 'advanced',
    promptHint: 'Add a map location picker field',
    icon: 'geo',
    available: AVAILABLE,
    tags: ['location', 'pin', 'place', 'picker', 'gps', 'coordinates'],
  },
  {
    id: 'file_uploader',
    label: 'File uploader',
    description: 'Attach documents, PDFs, or other files.',
    category: 'advanced',
    promptHint: 'Add a file uploader field',
    icon: 'file',
    available: COMING_SOON,
    tags: ['upload', 'document', 'pdf', 'attachment', 'attach', 'browse'],
  },
  {
    id: 'audio_recorder',
    label: 'Audio recorder',
    description: 'Record short audio answers or notes.',
    category: 'advanced',
    promptHint: 'Add an audio recorder field',
    icon: 'voice',
    available: COMING_SOON,
    tags: ['voice', 'sound', 'record', 'microphone', 'mic', 'audio'],
  },
  {
    id: 'video_recorder',
    label: 'Video recorder',
    description: 'Record a short video response.',
    category: 'advanced',
    promptHint: 'Add a video recorder field',
    icon: 'camera',
    available: COMING_SOON,
    tags: ['camera', 'record', 'clip', 'video', 'capture', 'film'],
  },
  {
    id: 'rich_text',
    label: 'Rich text editor',
    description: 'Formatted multi-line text with basic styling.',
    category: 'advanced',
    promptHint: 'Add a rich text editor field',
    icon: 'textarea',
    available: COMING_SOON,
    tags: ['formatted', 'editor', 'wysiwyg', 'html', 'bold', 'italic'],
  },
  {
    id: 'document_scanner',
    label: 'Document scanner',
    description: 'Scan a document with the device camera.',
    category: 'advanced',
    promptHint: 'Add a document scanner field',
    icon: 'file',
    available: COMING_SOON,
    tags: ['scan', 'camera', 'document', 'ocr', 'paper', 'id'],
  },
  {
    id: 'drawing',
    label: 'Drawing pad',
    description: 'Freehand drawing or sketch input.',
    category: 'advanced',
    promptHint: 'Add a drawing pad field',
    icon: 'signature',
    available: COMING_SOON,
    tags: ['sketch', 'pad', 'freehand', 'canvas', 'draw', 'doodle'],
  },
  {
    id: 'signature',
    label: 'Signature',
    description: 'Capture a handwritten or drawn signature.',
    category: 'advanced',
    promptHint: 'Add a signature field',
    icon: 'signature',
    available: COMING_SOON,
    tags: ['sign', 'autograph', 'draw', 'handwritten', 'consent', 'approve'],
  },
  {
    id: 'captcha',
    label: 'Captcha',
    description: 'Bot-prevention challenge before submit.',
    category: 'advanced',
    promptHint: 'Add a captcha field',
    icon: 'text',
    available: COMING_SOON,
    tags: ['bot', 'security', 'verification', 'spam', 'challenge', 'recaptcha'],
  },
];

export const LAYOUT_FIELD_CATALOG = FIELD_CATALOG.filter((item) => item.category === 'layout');
export const BASIC_FIELD_CATALOG = FIELD_CATALOG.filter((item) => item.category === 'basic');
export const INTERMEDIATE_FIELD_CATALOG = FIELD_CATALOG.filter(
  (item) => item.category === 'intermediate',
);
export const ADVANCED_FIELD_CATALOG = FIELD_CATALOG.filter((item) => item.category === 'advanced');

export function matchesFieldCatalogSearch(item: FieldCatalogItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) {
    return true;
  }
  return (
    item.label.toLowerCase().includes(q) ||
    item.description.toLowerCase().includes(q) ||
    item.id.toLowerCase().includes(q) ||
    item.tags.some((tag) => tag.includes(q))
  );
}

const CATALOG_ICON_SRC: Record<FieldCatalogIcon, string> = {
  heading: 'assets/icons/components/heading.svg',
  paragraph: 'assets/icons/components/paragraph.svg',
  divider: 'assets/icons/components/divider.svg',
  spacer: 'assets/icons/components/spacer.svg',
  text: 'assets/icons/components/text-input.svg',
  textarea: 'assets/icons/components/textarea.svg',
  email: 'assets/icons/components/email.svg',
  phone: 'assets/icons/components/phone.svg',
  number: 'assets/icons/components/number.svg',
  checkbox: 'assets/icons/components/checkbox.svg',
  checkbox_group: 'assets/icons/components/checkbox-multiple.svg',
  radio_group: 'assets/icons/components/radio.svg',
  select: 'assets/icons/components/dropdown.svg',
  search: 'assets/icons/search.svg',
  switch: 'assets/icons/components/switch.svg',
  single_choice_grid: 'assets/icons/components/radio.svg',
  multi_choice_grid: 'assets/icons/components/checkbox-multiple.svg',
  number_grid: 'assets/icons/components/number-grid.svg',
  date: 'assets/icons/components/date.svg',
  time: 'assets/icons/components/time.svg',
  date_range: 'assets/icons/components/date-range.svg',
  slider: 'assets/icons/components/slider.svg',
  image_picker: 'assets/icons/components/image-picker.svg',
  range_slider: 'assets/icons/range-slider.svg',
  image: 'assets/icons/components/image-picker.svg',
  camera: 'assets/icons/components/camera.svg',
  geo: 'assets/icons/components/geo-location.svg',
  voice: 'assets/icons/components/voice-recorder.svg',
  file: 'assets/icons/components/file-uploader.svg',
  signature: 'assets/icons/components/signature.svg',
};

const DEFAULT_CATALOG_ICON_SRC = 'assets/icons/components/text-input.svg';

export function catalogIconSrc(icon: FieldCatalogIcon | string): string {
  return CATALOG_ICON_SRC[icon as FieldCatalogIcon] ?? DEFAULT_CATALOG_ICON_SRC;
}
